import type { ArdoqId, APISlideAttributes } from '@ardoq/api-types';
import type { ContextShape } from '@ardoq/data-model';
import {
  persistentDerivedStream,
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { isEqual } from 'lodash';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { selectedModule$ } from 'appContainer/selectedModule$';
import { AppModules, type PayloadShowAppModule } from 'appContainer/types';
import { unmatchedLocation } from 'router/declarative/location';
import {
  type AppRouterLocation,
  location$,
} from 'router/declarative/location$';
import {
  clearSelectedSlide,
  type PayloadSetLoadedSlideId,
  type PayloadSetSlideId,
  setLoadedSlideId,
  setSelectedSlideId,
} from 'streams/context/ContextActions';
import { contextPresentationId$ } from 'streams/context/context$';
import { slides$ } from 'streams/slides/slides$';
import type { CollectionStream } from 'streams/utils/streamUtils';
import { toSelectedSlideId } from './toSelectedSlideId';

type OpenedSlideState = {
  openedSlideId: ArdoqId | null;
  loadedSlideId: ArdoqId | null;
};

const initialOpenedSlideState: OpenedSlideState = {
  openedSlideId: null,
  loadedSlideId: null,
};

const handleSetSelectedSlideId = (
  state: OpenedSlideState,
  { selectedSlideId }: PayloadSetSlideId
): OpenedSlideState => ({ ...state, openedSlideId: selectedSlideId });

const handleClearSelectedSlide = (
  state: OpenedSlideState
): OpenedSlideState => ({
  ...state,
  openedSlideId: null,
});

const handleSetLoadedSlideId = (
  state: OpenedSlideState,
  { loadedSlideId }: PayloadSetLoadedSlideId
): OpenedSlideState => ({
  ...state,
  loadedSlideId,
});

const handleSelectedModuleChange = (
  state: OpenedSlideState,
  { selectedModule }: PayloadShowAppModule
): OpenedSlideState => {
  if (
    [
      AppModules.HOME,
      AppModules.USER_SETTINGS,
      AppModules.MANAGE_ORGANIZATION,
    ].includes(selectedModule)
  ) {
    return initialOpenedSlideState;
  }
  return state;
};

const handlePresentationChanged = (
  state: OpenedSlideState,
  { presentationId }: ContextShape
): OpenedSlideState => (presentationId ? state : initialOpenedSlideState);

/**
 * The slide last opened from the presentation sidebar or restored from a
 * workspace URL, and the slide whose data is loaded.
 */
const openedSlide$ = persistentReducedStream<OpenedSlideState>(
  'openedSlide$',
  initialOpenedSlideState,
  [
    reducer(setLoadedSlideId, handleSetLoadedSlideId),
    reducer(setSelectedSlideId, handleSetSelectedSlideId),
    reducer(clearSelectedSlide, handleClearSelectedSlide),
    streamReducer(contextPresentationId$, handlePresentationChanged),
    streamReducer(selectedModule$, handleSelectedModuleChange),
  ]
);

export type SelectedSlideState = {
  selectedSlideId: ArdoqId | null;
  loadedSlideId: ArdoqId | null;
};

const toSelectedSlideState = ([
  { openedSlideId, loadedSlideId },
  { byId },
  { selectedModule },
  location,
]: [
  OpenedSlideState,
  CollectionStream<APISlideAttributes>,
  PayloadShowAppModule,
  AppRouterLocation,
]): SelectedSlideState => ({
  selectedSlideId: toSelectedSlideId({
    openedSlideId,
    slidesById: byId,
    selectedModule,
    location,
  }),
  loadedSlideId,
});

export const selectedSlide$ = persistentDerivedStream<SelectedSlideState>(
  'selectedSlide$',
  combineLatest([
    openedSlide$,
    slides$,
    selectedModule$,
    location$.pipe(startWith(unmatchedLocation)),
  ]).pipe(map(toSelectedSlideState), distinctUntilChanged(isEqual)),
  { selectedSlideId: null, loadedSlideId: null }
);
